import { useId, useState, useEffect, useRef, Fragment } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { parseApiError, formatName } from '@/common/utils';
import { customerTypeLabel } from '../utils/customerTypeLabel';
import {
  Button,
  ConfirmDeleteModal,
  Modal,
  CustomSelect,
  SearchInput,
  SideDrawer,
  SkeletonText,
} from '@/components';
import { ContactForm } from '../components/ContactForm';
import { WindexYearSync } from '../components/Integrations/WindexYearSync';
import {
  emptyWindexFilters,
  naturalSortAscending,
  normalizeContactList,
  normalizeSyncedYears,
  windexFilterBlocked,
  windexFilterMessage,
  windexFiltersToDrop,
  windexQueryParams,
} from '../components/Integrations/windexContactFilters';
import { ContactDetailsModal } from '../components/ContactDetailsModal';
import { CustomerTypesModal } from '../components/CustomerTypesModal';
import { ContactsExportModal } from '../components/ContactsExportModal';
import {
  createContact,
  getContacts,
  exportWhatsAppClients,
  updateContact,
  deleteContact,
  previewClientBulkAction,
  runClientBulkAction,
  importContacts,
  getCustomerTypes,
  getWindexStatus,
  getWindexContactYears,
  getWindexPendingFees,
  getGoogleWorkspacePickerSession,
  importGoogleWorkspaceClients,
} from '../api';
import * as XLSX from 'xlsx';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { useOutsideClick, usePermissions } from '@/common/hooks';

import {
  PlusIcon,
  ImportIcon,
  ExportIcon,
  User,
  FilterIcon,
  // PaperAirplaneIcon,
} from '../components/Icons';
import editIcon from '../assets/editIcon.png';
import trashIcon from '../assets/trashIcon.png';
import {
  DashboardPage,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
} from '../components/PremiumDesign';
import { PermissionGuard, V2Checkbox, V2Table } from '@/common/components';
import { PERMISSIONS } from '@/common/config/permissions';
import { GoogleDriveIcon } from '../crm/components/GoogleSheetSelector';
import { useGoogleWorkspaceConnected } from '../hooks/useGoogleWorkspaceConnected';
import {
  loadGoogleApiScript,
  openSpreadsheetPicker,
  parseGoogleWorkspaceError,
  pickerSessionErrorMessage,
} from '../crm/utils/googleWorkspaceUtils';

const WINDEX_FILTER_FIELDS = [
  { key: 'year', label: 'Academic Year' },
  { key: 'class', label: 'Class' },
  { key: 'pendingFees', label: 'Pending Fees' },
];

const IMPORT_PREVIEW_ROWS = [
  {
    sr: 1,
    phone: '+91 9876543210',
    name: 'rakesh patel',
    email: 'rakesh@example.com',
    customerType: 'B2B, B2C',
  },
  {
    sr: 2,
    phone: '+91 9123456789',
    name: 'priya',
    email: 'priya@example.com',
    customerType: 'B2C',
  },
  {
    sr: 3,
    phone: '+91 9988776655',
    name: 'amit',
    email: '',
    customerType: 'Dealer',
  },
  {
    sr: 4,
    phone: '+91 9098765432',
    name: 'neha',
    email: 'neha@example.com',
    customerType: 'Wholesaler',
  },
];

function FileDocIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="8" y1="9" x2="10" y2="9" />
    </svg>
  );
}

const formatErrorString = (errStr) => {
  if (!errStr) return 'Unknown error';
  if (typeof errStr !== 'string') {
    try {
      return JSON.stringify(errStr);
    } catch {
      return 'Unknown error';
    }
  }

  if (errStr.includes('ErrorDetail')) {
    const matches = [...errStr.matchAll(/string='([^']+)'/g)];
    if (matches.length > 0) {
      return matches.map((m) => m[1]).join(', ');
    }
  }

  return errStr;
};

const CONTACTS_COLUMNS = [
  { id: 'select', label: '', defaultWidth: 48, minWidth: 48, resizable: false },
  {
    id: 'sr',
    label: 'Sr No.',
    defaultWidth: 72,
    minWidth: 60,
    resizable: true,
  },
  {
    id: 'name',
    label: 'Name',
    defaultWidth: 230,
    minWidth: 160,
    resizable: true,
  },
  {
    id: 'email',
    label: 'Email',
    defaultWidth: 240,
    minWidth: 160,
    resizable: true,
  },
  {
    id: 'phone',
    label: 'Mobile',
    defaultWidth: 160,
    minWidth: 130,
    resizable: true,
  },
  {
    id: 'pendingFees',
    label: 'Pending fee',
    defaultWidth: 130,
    minWidth: 110,
    resizable: true,
  },
  {
    id: 'type',
    label: 'Type',
    defaultWidth: 160,
    minWidth: 120,
    resizable: true,
  },
  {
    id: 'createdAt',
    label: 'Created At',
    defaultWidth: 160,
    minWidth: 130,
    resizable: true,
  },
  {
    id: 'modifiedAt',
    label: 'Modified At',
    defaultWidth: 160,
    minWidth: 130,
    resizable: true,
  },
  {
    id: 'actions',
    label: 'Actions',
    defaultWidth: 96,
    minWidth: 80,
    resizable: false,
  },
];

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = [
  'bg-[#E8F5E9] text-[#2E7D32]',
  'bg-[#E0F2FE] text-[#0369A1]',
  'bg-[#F3E8FF] text-[#7E22CE]',
  'bg-[#FEF3C7] text-[#B45309]',
  'bg-[#FCE7F3] text-[#BE185D]',
  'bg-[#E0E7FF] text-[#4338CA]',
];

const getAvatarBg = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
};

export function ContactsPage() {
  const contactDialogId = useId();
  const importDialogId = useId();
  const { can } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingContact, setViewingContact] = useState(null);
  const [isTypesModalOpen, setIsTypesModalOpen] = useState(false);
  const [isImportInfoModalOpen, setIsImportInfoModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [apiErrors, setApiErrors] = useState({});

  const [contactToDelete, setContactToDelete] = useState(null);
  const [contactsToDeleteBulk, setContactsToDeleteBulk] = useState(false);
  const [bulkTypeOpen, setBulkTypeOpen] = useState(false);
  const [bulkTypeIds, setBulkTypeIds] = useState([]);
  const [bulkTypeError, setBulkTypeError] = useState('');
  const [matchedCount, setMatchedCount] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [selectMode, setSelectMode] = useState(null);
  const [pickedIds, setPickedIds] = useState(() => new Set());
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [windexFilters, setWindexFilters] = useState(emptyWindexFilters);
  const [activeWindexFilters, setActiveWindexFilters] = useState([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);
  useOutsideClick(
    filterMenuRef,
    () => setIsFilterMenuOpen(false),
    isFilterMenuOpen,
  );
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState(null);
  const [customerTypeSearch, setCustomerTypeSearch] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [isDrivePickerLoading, setIsDrivePickerLoading] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const clearSelection = () => {
    setSelectMode(null);
    setPickedIds(new Set());
    setExcludedIds(new Set());
  };

  const contactsFiltered =
    Boolean(debouncedSearch) ||
    Boolean(typeFilter) ||
    activeWindexFilters.length > 0;

  const clearContactsFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setTypeFilter(null);
    setActiveWindexFilters([]);
    setWindexFilters(emptyWindexFilters());
    setPage(1);
    clearSelection();
  };

  const isSelected = (id) =>
    selectMode === 'all_matching' ? !excludedIds.has(id) : pickedIds.has(id);

  const toggleSelection = (id) => {
    if (selectMode === 'all_matching') {
      const next = new Set(excludedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (next.size > 1000) {
        toast.error('At most 1000 excluded contacts.');
        return;
      }
      setExcludedIds(next);
      return;
    }
    const next = new Set(pickedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    if (next.size > 1000) {
      toast.error('At most 1000 selected contacts.');
      return;
    }
    setPickedIds(next);
    setSelectMode(next.size ? 'ids' : null);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to page 1 on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    clearSelection();
  }, [debouncedSearch, typeFilter]);

  let currentCompanyId =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!currentCompanyId) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        currentCompanyId = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore parsing error
    }
  }

  const queryClient = useQueryClient();
  const googleWorkspaceConnected =
    useGoogleWorkspaceConnected(currentCompanyId);

  const { data: windexStatus } = useQuery({
    queryKey: ['windex-status', currentCompanyId],
    queryFn: () => getWindexStatus(currentCompanyId),
    enabled: !!currentCompanyId,
    retry: false,
  });
  const windexConnected = !!windexStatus?.is_connected;

  useEffect(() => {
    if (windexConnected) return;
    setActiveWindexFilters([]);
    setWindexFilters(emptyWindexFilters());
  }, [windexConnected]);

  const feesOn = activeWindexFilters.includes('pendingFees');
  const feesNeedYear = feesOn && (!windexFilters.year || !windexFilters.class);

  const hasActions =
    can(PERMISSIONS.CONTACTS.UPDATE) || can(PERMISSIONS.CONTACTS.DELETE);

  const { data: yearsData, error: yearsError } = useQuery({
    queryKey: ['windex-contact-years', currentCompanyId],
    queryFn: () => getWindexContactYears(currentCompanyId),
    enabled: windexConnected && !!currentCompanyId,
    retry: false,
  });
  const syncedYears = normalizeSyncedYears(yearsData);

  const {
    data: contactsData,
    isLoading: isContactsInitialLoading,
    isFetching: isContactsFetching,
    error: contactsError,
  } = useQuery({
    queryKey: [
      'whatsapp-contacts',
      currentCompanyId,
      debouncedSearch,
      page,
      feesOn ? '' : typeFilter || '',
      windexFilters.year,
      windexFilters.class,
      feesOn,
    ],
    queryFn: async () => {
      if (!currentCompanyId) return { results: [] };
      const windexParams = windexQueryParams(windexFilters);
      if (feesOn) {
        const data = await getWindexPendingFees(currentCompanyId, {
          year: windexFilters.year,
          studentClass: windexParams.class,
        });
        return normalizeContactList(data);
      }
      const params = debouncedSearch ? { search: debouncedSearch } : {};
      if (page > 1) params.page = page;
      if (typeFilter) params.filter = typeFilter;
      Object.assign(params, windexParams);
      return getContacts(currentCompanyId, params);
    },
    enabled: !!currentCompanyId && !feesNeedYear,
    placeholderData: keepPreviousData,
  });

  const filterMessage = feesNeedYear
    ? 'Choose an academic year and class'
    : windexFilterMessage(contactsError) || windexFilterMessage(yearsError);

  const showDelayedSkeleton = useDelayedSkeleton(
    isContactsFetching && contactsData !== undefined,
    300,
    500,
  );
  const isContactsLoading = isContactsInitialLoading || showDelayedSkeleton;

  const { data: customerTypesData, isLoading: isCustomerTypesLoading } =
    useQuery({
      queryKey: ['customer-types', currentCompanyId, customerTypeSearch],
      queryFn: () =>
        getCustomerTypes(
          currentCompanyId,
          customerTypeSearch ? { search: customerTypeSearch } : {},
        ),
      enabled: !!currentCompanyId,
    });
  const customerTypes = customerTypesData || [];
  const typeFilterOptions = [
    { value: '', label: 'All Type' },
    ...customerTypes.map((t) => ({ value: t.name, label: t.name })),
  ];

  const handleTypeFilterChange = (val) => {
    setTypeFilter(val || null);
    setPage(1);
  };

  const contacts = contactsData?.results || [];
  const count = contactsData?.count || 0;

  const filteredContacts = feesOn
    ? contacts.slice((page - 1) * 10, page * 10)
    : contacts;

  const headerChecked =
    selectMode === 'all_matching' &&
    filteredContacts.length > 0 &&
    filteredContacts.every((c) => !excludedIds.has(c.id));

  const selectedCount =
    selectMode === 'all_matching'
      ? Math.max(0, count - excludedIds.size)
      : pickedIds.size;

  const buildSelection = () => {
    if (selectMode === 'all_matching') {
      return {
        mode: 'all_matching',
        excluded_ids: Array.from(excludedIds),
        search: debouncedSearch || '',
        filter: typeFilter || '',
        segment: null,
        import_start_date: null,
        import_end_date: null,
      };
    }
    return { mode: 'ids', client_ids: Array.from(pickedIds) };
  };

  const selectionOverLimit = (selection) => {
    const ids =
      selection.mode === 'ids' ? selection.client_ids : selection.excluded_ids;
    return (ids?.length || 0) > 1000;
  };

  const bulkErrorText = (err) => {
    const data = err?.response?.data;
    const clientIds = data?.selection?.client_ids;
    if (clientIds) return Array.isArray(clientIds) ? clientIds[0] : clientIds;
    const types = data?.customer_type_ids;
    if (types) return Array.isArray(types) ? types[0] : types;
    return parseApiError(err).message;
  };

  const handleToggleSelectAll = () => {
    if (headerChecked) {
      clearSelection();
      return;
    }
    setSelectMode('all_matching');
    setPickedIds(new Set());
    setExcludedIds(new Set());
  };

  const previewMatchCount = async () => {
    const selection = buildSelection();
    if (selectionOverLimit(selection)) {
      toast.error('At most 1000 ids.');
      return null;
    }
    setIsPreviewing(true);
    setMatchedCount(null);
    try {
      const data = await previewClientBulkAction({
        companyId: currentCompanyId,
        selection,
      });
      setMatchedCount(data?.matched_count ?? 0);
      return data?.matched_count ?? 0;
    } catch (err) {
      toast.error(bulkErrorText(err) || 'Failed to count selected contacts.');
      return null;
    } finally {
      setIsPreviewing(false);
    }
  };

  const finishBulk = (updatedCount) => {
    const pageSize = 10;
    const remaining = Math.max(0, (count || 0) - (updatedCount || 0));
    const maxPage = Math.max(1, Math.ceil(remaining / pageSize));
    const nextPage = Math.min(page, maxPage);
    const pageChanged = nextPage !== page;
    clearSelection();
    setContactsToDeleteBulk(false);
    setBulkTypeOpen(false);
    setMatchedCount(null);
    if (pageChanged) setPage(nextPage);
    queryClient.invalidateQueries({
      queryKey: ['whatsapp-contacts', currentCompanyId],
      refetchType: pageChanged ? 'none' : 'active',
    });
  };

  const { mutate: executeBatchDelete, isPending: isDeletingBatch } =
    useMutation({
      mutationFn: () => {
        if (!can(PERMISSIONS.CONTACTS.DELETE)) {
          toast.error('You do not have permission to perform this action.');
          return Promise.reject(new Error('Unauthorized'));
        }
        return runClientBulkAction({
          companyId: currentCompanyId,
          action: 'delete',
          selection: buildSelection(),
        });
      },
      onSuccess: (data) => {
        const updatedCount = data?.updated_count ?? matchedCount ?? 0;
        toast.success(`Deleted ${updatedCount} contact(s).`);
        finishBulk(updatedCount);
      },
      onError: (err) => {
        toast.error(
          bulkErrorText(err) || 'Failed to delete selected contact(s).',
        );
      },
    });

  const { mutate: executeBulkTypes, isPending: isSettingTypes } = useMutation({
    mutationFn: () => {
      if (!can(PERMISSIONS.CONTACTS.UPDATE)) {
        toast.error('You do not have permission to perform this action.');
        return Promise.reject(new Error('Unauthorized'));
      }
      return runClientBulkAction({
        companyId: currentCompanyId,
        action: 'set_customer_types',
        selection: buildSelection(),
        payload: {
          customer_type_ids: bulkTypeIds.map((id) => Number(id)),
        },
      });
    },
    onSuccess: (data) => {
      const updatedCount = data?.updated_count ?? matchedCount ?? 0;
      toast.success(`Updated ${updatedCount} contact(s).`);
      finishBulk(updatedCount);
    },
    onError: (err) => {
      const data = err?.response?.data;
      const types = data?.customer_type_ids;
      if (types) {
        setBulkTypeError(Array.isArray(types) ? types[0] : types);
      }
      toast.error(bulkErrorText(err) || 'Failed to update customer types.');
    },
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const { mutate: handleCreateContact, isPending: isCreating } = useMutation({
    mutationFn: (payload) => {
      if (!can(PERMISSIONS.CONTACTS.CREATE)) {
        toast.error('You do not have permission to perform this action.');
        return Promise.reject(new Error('Unauthorized'));
      }
      return createContact(payload);
    },
    onSuccess: () => {
      toast.success('Contact created successfully!');
      setApiErrors({});
      setIsModalOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-contacts', currentCompanyId],
      });
    },
    onError: (error) => {
      const { message, fieldErrors } = parseApiError(error);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to create contact.');
    },
  });

  const { mutate: handleUpdateContact, isPending: isUpdating } = useMutation({
    mutationFn: (payload) => {
      if (!can(PERMISSIONS.CONTACTS.UPDATE)) {
        toast.error('You do not have permission to perform this action.');
        return Promise.reject(new Error('Unauthorized'));
      }
      return updateContact(payload);
    },
    onSuccess: () => {
      toast.success('Contact updated successfully!');
      setApiErrors({});
      setIsModalOpen(false);
      setSelectedContact(null);
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-contacts', currentCompanyId],
      });
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      toast.error(message || 'Failed to update contact.');
    },
  });

  const { mutate: executeDeleteContact } = useMutation({
    mutationFn: (id) => {
      if (!can(PERMISSIONS.CONTACTS.DELETE)) {
        toast.error('You do not have permission to perform this action.');
        return Promise.reject(new Error('Unauthorized'));
      }
      return deleteContact(id);
    },
    onSuccess: () => {
      toast.success('Contact deleted successfully!');
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-contacts', currentCompanyId],
      });
    },
    onError: (err) => {
      const errorData = err.response?.data;
      toast.error(
        errorData?.message || errorData?.detail || 'Failed to delete contact.',
      );
    },
  });

  const handleDelete = (contactId) => {
    setContactToDelete(contactId);
  };

  const { mutate: handleImportContacts, isPending: isImporting } = useMutation({
    mutationFn: importContacts,
    onSuccess: (data) => {
      toast.success('Contacts imported successfully!');
      setImportResult(data);
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-contacts', currentCompanyId],
      });
    },
    onError: (err) => {
      const errorData = err.response?.data;
      toast.error(
        errorData?.message || errorData?.detail || 'Failed to import contacts.',
      );
    },
  });

  const isPending = isCreating || isUpdating || isImporting;

  const handleEditClick = (contact) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  };

  const rejectLegacyXls = (name) =>
    /\.xls$/i.test(name) && !/\.xlsx$/i.test(name);

  const handleImportFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = e.target.files[0];
      if (rejectLegacyXls(selected.name)) {
        toast.error(
          'Save this file as .xlsx or CSV. Old .xls files are not supported.',
        );
        return;
      }
      setImportFile(selected);
    }
  };

  const openClientDrivePicker = async () => {
    if (!currentCompanyId) {
      toast.error('Company ID is missing.');
      return;
    }
    const apiKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY;
    if (!apiKey) {
      toast.error('VITE_GOOGLE_PICKER_API_KEY is missing.');
      return;
    }
    setIsDrivePickerLoading(true);
    try {
      const session = await getGoogleWorkspacePickerSession(currentCompanyId);
      await loadGoogleApiScript();
      await openSpreadsheetPicker({
        accessToken: session.access_token,
        appId: session.app_id,
        apiKey,
        onPicked: async (file) => {
          if (rejectLegacyXls(file.name || '')) {
            toast.error(
              'Save this file as .xlsx or CSV. Old .xls files are not supported.',
            );
            return;
          }
          try {
            const data = await importGoogleWorkspaceClients({
              companyId: currentCompanyId,
              payload: {
                spreadsheet_id: file.id,
                sheet_name: 'Sheet1',
              },
            });
            toast.success('Contacts imported successfully!');
            setImportResult(data);
            setImportFile(null);
            setIsImportInfoModalOpen(false);
            queryClient.invalidateQueries({
              queryKey: ['whatsapp-contacts', currentCompanyId],
            });
          } catch (err) {
            const errorData = err.response?.data;
            toast.error(
              errorData?.message ||
                errorData?.detail ||
                parseGoogleWorkspaceError(err).message ||
                'Failed to import contacts.',
            );
          }
        },
      });
    } catch (err) {
      const sessionMessage = pickerSessionErrorMessage(err);
      toast.error(
        sessionMessage ||
          parseGoogleWorkspaceError(err).message ||
          'Could not open Google Picker.',
      );
    } finally {
      setIsDrivePickerLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      {
        Sr: 1,
        Phone: '+91 9876543210',
        Name: 'rakesh patel',
        Email: 'rakesh@example.com',
        CustomerType: 'B2B, B2C',
      },
      {
        Sr: 2,
        Phone: '+91 9123456789',
        Name: 'priya',
        Email: 'priya@example.com',
        CustomerType: 'B2C',
      },
      {
        Sr: 3,
        Phone: '+91 9988776655',
        Name: 'amit',
        Email: '',
        CustomerType: 'Dealer',
      },
      {
        Sr: 4,
        Phone: '+91 9098765432',
        Name: 'neha',
        Email: 'neha@example.com',
        CustomerType: 'Wholesaler',
      },
      {
        Sr: 5,
        Phone: '+91 9765432109',
        Name: 'karan',
        Email: '',
        CustomerType: 'Hospitality_check',
      },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
    XLSX.writeFile(wb, 'sample_contacts.xlsx');
  };

  const handleCloseImportModal = () => {
    setImportFile(null);
    setIsImportInfoModalOpen(false);
  };

  const handleSubmitImport = () => {
    if (!importFile) return;
    handleImportContacts({
      companyId: currentCompanyId,
      file: importFile,
    });
    handleCloseImportModal();
  };

  const handleExportClients = async (format = 'xlsx') => {
    if (!currentCompanyId) return;
    setIsExporting(true);

    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter) params.filter = typeFilter;

      const res = await exportWhatsAppClients(currentCompanyId, params);
      const headers = res?.headers || [];
      let rows = res?.data || [];

      const getRowId = (r) => r?.ID ?? r?.id ?? r?.Id;

      // If user specifically picked individual rows via checkboxes, filter export to those IDs
      if (selectMode === 'ids' && pickedIds.size > 0) {
        rows = rows.filter((r) => {
          const id = getRowId(r);
          return (
            pickedIds.has(id) ||
            pickedIds.has(String(id)) ||
            (typeof id === 'string' &&
              !isNaN(Number(id)) &&
              pickedIds.has(Number(id)))
          );
        });
      } else if (selectMode === 'all_matching' && excludedIds.size > 0) {
        rows = rows.filter((r) => {
          const id = getRowId(r);
          return (
            !excludedIds.has(id) &&
            !excludedIds.has(String(id)) &&
            !(
              typeof id === 'string' &&
              !isNaN(Number(id)) &&
              excludedIds.has(Number(id))
            )
          );
        });
      }

      if (!rows || rows.length === 0) {
        toast.error('No contacts to export.');
        return;
      }

      const timestamp = new Date().toISOString().slice(0, 10);
      const fileName = `whatsapp_clients_${timestamp}`;

      if (format === 'csv') {
        const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
        const csvString = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob(['\uFEFF' + csvString], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Clients');
        XLSX.writeFile(wb, `${fileName}.xlsx`);
      }

      setIsExportModalOpen(false);
      toast.success(
        `Exported ${rows.length} contact${rows.length === 1 ? '' : 's'} as ${format.toUpperCase()}!`,
      );
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        parseApiError(err).message ||
        'Failed to export contacts.';
      toast.error(errorMsg);
    } finally {
      setIsExporting(false);
    }
  };

  const tableColumns = (() => {
    const sk = {
      select: <SkeletonText width="w-4" height="h-4" className="mx-auto" />,
      sr: <SkeletonText width="w-7" height="h-3.5" />,
      name: (
        <div className="flex items-center gap-2.5">
          <SkeletonText
            width="w-8"
            height="h-8"
            className="!rounded-full shrink-0"
          />
          <SkeletonText width="w-28" height="h-4" />
        </div>
      ),
      email: <SkeletonText width="w-36" height="h-4" />,
      phone: <SkeletonText width="w-24" height="h-4" />,
      pendingFees: <SkeletonText width="w-16" height="h-4" />,
      type: (
        <SkeletonText width="w-16" height="h-5" className="!rounded-full" />
      ),
      createdAt: <SkeletonText width="w-24" height="h-3.5" />,
      modifiedAt: <SkeletonText width="w-24" height="h-3.5" />,
      actions: (
        <div className="flex items-center justify-center gap-2">
          <SkeletonText width="w-6" height="h-6" className="!rounded-md" />
          <SkeletonText width="w-6" height="h-6" className="!rounded-md" />
        </div>
      ),
    };

    return CONTACTS_COLUMNS.filter((col) => {
      if (col.id === 'pendingFees' && !feesOn) return false;
      if (col.id === 'actions' && !hasActions) return false;
      return true;
    }).map((col) => ({
      ...col,
      align: col.id === 'select' || col.id === 'actions' ? 'center' : 'left',
      stopPropagation: col.id === 'select' || col.id === 'actions',
      skeleton: sk[col.id],
      header:
        col.id === 'select'
          ? () => (
              <V2Checkbox
                checked={headerChecked}
                onChange={handleToggleSelectAll}
                isHeader
              />
            )
          : undefined,
      cell: (contact, index) => {
        if (col.id === 'select') {
          return (
            <V2Checkbox
              checked={isSelected(contact.id)}
              onChange={() => toggleSelection(contact.id)}
            />
          );
        }
        if (col.id === 'sr') {
          return (
            <span className="truncate block text-xs font-medium text-[#64748B]">
              {(page - 1) * 10 + index + 1}
            </span>
          );
        }
        if (col.id === 'name') {
          return (
            <div className="flex items-center gap-2.5 overflow-hidden">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs shrink-0 select-none ${getAvatarBg(contact.name)}`}
                aria-hidden="true"
              >
                {getInitials(contact.name)}
              </div>
              <span
                className="truncate block font-medium text-sm text-[#0F172A] hover:text-[#2E7D32] transition-colors"
                title={contact.name || '-'}
              >
                {formatName(contact.name) || '-'}
              </span>
            </div>
          );
        }
        if (col.id === 'email') {
          return (
            <span
              className={`truncate block text-sm ${contact.email ? 'text-[#1E293B]' : 'text-text-muted'}`}
              title={contact.email || '-'}
            >
              {contact.email || '-'}
            </span>
          );
        }
        if (col.id === 'phone') {
          return (
            <span
              className="truncate block text-sm text-[#1E293B]"
              title={contact.phone || '-'}
            >
              {contact.phone || '-'}
            </span>
          );
        }
        if (col.id === 'pendingFees') {
          return (
            <span
              className="truncate block text-sm font-semibold text-[#DC2626]"
              title={contact.pending_fee ?? '-'}
            >
              {contact.pending_fee != null ? `₹${contact.pending_fee}` : '-'}
            </span>
          );
        }
        if (col.id === 'type') {
          const typeLabel = customerTypeLabel(contact);
          return typeLabel ? (
            <div
              className="flex items-center gap-1.5 overflow-hidden"
              title={typeLabel}
            >
              {typeLabel.split(',').map((item, tIdx) => (
                <span
                  key={tIdx}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#334155] border border-[#E2E8F0] whitespace-nowrap truncate max-w-[120px]"
                >
                  {item.trim()}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-text-muted text-xs">-</span>
          );
        }
        if (col.id === 'createdAt') {
          return (
            <span
              className="truncate block text-xs text-[#64748B]"
              title={formatDate(contact.created_at)}
            >
              {formatDate(contact.created_at)}
            </span>
          );
        }
        if (col.id === 'modifiedAt') {
          return (
            <span
              className="truncate block text-xs text-[#64748B]"
              title={formatDate(contact.updated_at)}
            >
              {formatDate(contact.updated_at)}
            </span>
          );
        }
        if (col.id === 'actions') {
          return (
            <div className="flex items-center justify-center gap-1.5">
              <PermissionGuard permission={PERMISSIONS.CONTACTS.UPDATE}>
                <button
                  onClick={() => handleEditClick(contact)}
                  className="p-1.5 hover:bg-[#F1F5F9] rounded-lg transition-colors focus:outline-none shrink-0"
                  title="Edit Contact"
                >
                  <img
                    src={editIcon}
                    alt="Edit"
                    className="w-4 h-4 object-contain shrink-0"
                  />
                </button>
              </PermissionGuard>
              <PermissionGuard permission={PERMISSIONS.CONTACTS.DELETE}>
                <button
                  onClick={() => handleDelete(contact.id)}
                  className="p-1.5 hover:bg-[#FEEFEF] rounded-lg transition-colors focus:outline-none shrink-0"
                  title="Delete Contact"
                >
                  <img
                    src={trashIcon}
                    alt="Delete"
                    className="w-4 h-4 object-contain shrink-0"
                  />
                </button>
              </PermissionGuard>
            </div>
          );
        }
        return null;
      },
    }));
  })();

  return (
    <DashboardPage className="contacts-page h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="mb-4 flex w-full min-w-0 flex-col gap-3">
        <div className="flex w-full min-w-0 flex-col gap-3 xl:flex-row xl:items-center xl:justify-between shrink-0">
          <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto xl:flex-1">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Name or Phone"
              className="w-full sm:w-64 min-w-0"
              inputClassName="h-10"
            />
            <CustomSelect
              options={typeFilterOptions}
              value={typeFilter || ''}
              onChange={handleTypeFilterChange}
              placeholder="All Type"
              searchable
              onSearchChange={setCustomerTypeSearch}
              isLoading={isCustomerTypesLoading}
              hideCheckIcon
              className="h-10 shrink-0 min-w-[130px]"
              buttonClassName="h-10 border border-border-default bg-form-background-light rounded-xl w-full flex items-center justify-between px-4 text-sm font-medium text-text-primary focus:outline-none transition-all hover:border-border-active"
              textSizeClass="text-sm font-medium"
            />
            {windexConnected ? (
              <div className="relative shrink-0" ref={filterMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsFilterMenuOpen((prev) => !prev)}
                  aria-expanded={isFilterMenuOpen}
                  aria-haspopup="menu"
                  className={`relative h-10 px-3.5 sm:px-4 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors shrink-0 focus:outline-none ${
                    isFilterMenuOpen || activeWindexFilters.length > 0
                      ? 'bg-primary text-white hover:bg-primary-hover'
                      : 'bg-[#F5F5F5] text-[#1F2241] hover:brightness-95'
                  }`}
                >
                  <FilterIcon className="w-4 h-4" />
                  Filter
                </button>
                {isFilterMenuOpen ? (
                  <div
                    role="menu"
                    className="absolute left-0 top-[46px] z-40 w-56 bg-white border border-[#EBF4EA] rounded-xl shadow-xl py-2"
                  >
                    <p className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide">
                      Filter by
                    </p>
                    {WINDEX_FILTER_FIELDS.filter(
                      (field) => !activeWindexFilters.includes(field.key),
                    ).map((field) => {
                      const blocked = windexFilterBlocked(
                        field.key,
                        activeWindexFilters,
                      );
                      return (
                        <button
                          key={field.key}
                          type="button"
                          role="menuitem"
                          disabled={!!blocked}
                          title={blocked || undefined}
                          onClick={() => {
                            if (blocked) return;
                            setActiveWindexFilters((prev) =>
                              prev.includes(field.key)
                                ? prev
                                : [...prev, field.key],
                            );
                            if (field.key === 'pendingFees') {
                              setPage(1);
                              clearSelection();
                            }
                            setIsFilterMenuOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                            blocked
                              ? 'cursor-not-allowed text-[#868686]'
                              : 'text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D]'
                          }`}
                        >
                          {field.label}
                        </button>
                      );
                    })}
                    {activeWindexFilters.length ===
                    WINDEX_FILTER_FIELDS.length ? (
                      <p className="px-4 py-2 text-sm text-text-muted">
                        All filters added
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {filterMessage ? (
              <p className="w-full text-xs text-rose-600">{filterMessage}</p>
            ) : null}
            {selectedCount > 0 && (
              <div className="inline-flex items-center gap-2 shrink-0 animate-fadeIn">
                <span className="h-10 px-3 inline-flex items-center text-sm font-semibold text-text-secondary">
                  {selectedCount} item(s) selected
                </span>
                <PermissionGuard permission={PERMISSIONS.CONTACTS.UPDATE}>
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-10 shrink-0"
                    onClick={async () => {
                      setBulkTypeIds([]);
                      setBulkTypeError('');
                      setBulkTypeOpen(true);
                      const n = await previewMatchCount();
                      if (n == null) setBulkTypeOpen(false);
                    }}
                  >
                    Customer type
                  </Button>
                </PermissionGuard>
                <PermissionGuard permission={PERMISSIONS.CONTACTS.DELETE}>
                  <button
                    type="button"
                    onClick={async () => {
                      setContactsToDeleteBulk(true);
                      const n = await previewMatchCount();
                      if (n == null) setContactsToDeleteBulk(false);
                    }}
                    className="h-10 px-4 rounded-xl bg-[#FEEFEF] text-[#F04438] hover:bg-[#FDE2E2] transition-colors inline-flex items-center gap-2.5 font-semibold text-sm cursor-pointer border border-[#FECDCA]/60"
                    title="Delete Selected Contacts"
                  >
                    <svg
                      className="w-5 h-5 stroke-[2] stroke-[#F04438]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </PermissionGuard>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full xl:w-auto justify-start xl:justify-end shrink-0">
            <PermissionGuard permission={PERMISSIONS.CONTACTS.CREATE}>
              <Button
                variant="secondary"
                onClick={() => setIsImportInfoModalOpen(true)}
                disabled={isImporting}
                className="h-10 shrink-0 justify-center !px-3 sm:!px-4 !border-0 !bg-[#F5F5F5] !text-[#1F2241] hover:!bg-[#EBF4EA] !text-xs sm:!text-sm"
                icon={
                  isImporting ? (
                    <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                  ) : (
                    <ImportIcon className="h-4 w-4 shrink-0" />
                  )
                }
              >
                <span className="truncate">
                  {isImporting ? 'Importing...' : 'Import Contact'}
                </span>
              </Button>
            </PermissionGuard>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setIsExportModalOpen(true)}
              disabled={isExporting}
              className="h-10 shrink-0 justify-center !px-3 sm:!px-4 !border-0 !bg-[#F5F5F5] !text-[#1F2241] hover:!bg-[#EBF4EA] !text-xs sm:!text-sm"
              icon={
                isExporting ? (
                  <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                ) : (
                  <ExportIcon className="h-4 w-4 shrink-0" />
                )
              }
            >
              <span className="truncate">
                {isExporting
                  ? 'Exporting...'
                  : selectedCount > 0
                    ? `Export (${selectedCount})`
                    : 'Export'}
              </span>
            </Button>
            <PermissionGuard permission={PERMISSIONS.CONTACTS.CREATE}>
              <Button
                variant="secondary"
                onClick={() => setIsTypesModalOpen(true)}
                className="h-10 !px-2 sm:!px-4 justify-center w-auto !text-xs sm:!text-sm overflow-hidden min-w-0"
                icon={<User className="h-4 w-4 shrink-0" />}
              >
                <span className="truncate">
                  <span className="hidden sm:inline">Manage </span>Customer Type
                </span>
              </Button>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.CONTACTS.CREATE}>
              <Button
                variant="primary"
                onClick={() => {
                  setSelectedContact(null);
                  setIsModalOpen(true);
                }}
                className="h-10 !px-2 sm:!px-4 justify-center w-auto !text-xs sm:!text-sm overflow-hidden min-w-0"
                icon={<PlusIcon />}
              >
                <span className="truncate">Create Contact</span>
              </Button>
            </PermissionGuard>
          </div>
        </div>
        {activeWindexFilters.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {activeWindexFilters.map((key, index) => {
              const field = WINDEX_FILTER_FIELDS.find(
                (item) => item.key === key,
              );
              if (!field) return null;
              const yearLocked = !windexFilters.year;
              const classOptions = (
                syncedYears.find((row) => row.year === windexFilters.year)
                  ?.classes || []
              )
                .slice()
                .sort(naturalSortAscending)
                .map((name) => ({ value: name, label: name }));
              return (
                <Fragment key={key}>
                  {index > 0 && (
                    <span
                      className="flex items-center justify-center w-5 h-5 rounded-full bg-[#F5F5F5] text-[#868686] shrink-0"
                      aria-hidden="true"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4.5v15m7.5-7.5h-15"
                        />
                      </svg>
                    </span>
                  )}
                  <div className="flex min-h-10 w-full max-w-full flex-wrap items-center gap-2 rounded-xl border border-[#EBF4EA] bg-white px-3 py-1.5 sm:w-auto sm:flex-nowrap sm:py-0 sm:pl-3 sm:pr-2">
                    <span className="text-sm font-semibold text-[#1F2241] whitespace-nowrap">
                      {field.label}
                    </span>
                    {key === 'pendingFees' ? null : (
                      <span className="text-sm text-[#868686]">is</span>
                    )}
                    {key === 'year' ? (
                      <CustomSelect
                        value={windexFilters.year || ''}
                        onChange={(val) => {
                          setWindexFilters((prev) => ({
                            ...prev,
                            year: val || '',
                            class: '',
                            pendingFees: val ? prev.pendingFees : false,
                          }));
                          setPage(1);
                          clearSelection();
                        }}
                        options={[
                          { value: '', label: 'All years' },
                          ...syncedYears.map((row) => ({
                            value: row.year,
                            label: row.year,
                          })),
                        ]}
                        dropdownWidth={160}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                    ) : null}
                    {key === 'class' ? (
                      <CustomSelect
                        value={yearLocked ? '' : windexFilters.class || ''}
                        disabled={yearLocked}
                        onChange={(val) => {
                          setWindexFilters((prev) => ({
                            ...prev,
                            class: val || '',
                          }));
                          setPage(1);
                          clearSelection();
                        }}
                        options={
                          yearLocked
                            ? []
                            : [
                                { value: '', label: 'All classes' },
                                ...classOptions,
                              ]
                        }
                        placeholder={
                          yearLocked
                            ? 'Select an academic year first'
                            : undefined
                        }
                        dropdownWidth={180}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                    ) : null}
                    <button
                      type="button"
                      aria-label={`Remove ${field.label} filter`}
                      onClick={() => {
                        const drop = windexFiltersToDrop(key);
                        setActiveWindexFilters((prev) =>
                          prev.filter((item) => !drop.includes(item)),
                        );
                        setWindexFilters((prev) => {
                          const next = { ...prev };
                          drop.forEach((item) => {
                            next[item] = item === 'pendingFees' ? false : '';
                          });
                          return next;
                        });
                        setPage(1);
                        clearSelection();
                      }}
                      className="text-[#868686] hover:text-[#1F2241] shrink-0"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </Fragment>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setActiveWindexFilters([]);
                setWindexFilters(emptyWindexFilters());
                setPage(1);
                clearSelection();
              }}
              className="text-sm font-semibold text-[#50AB3D] hover:text-primary-hover shrink-0"
            >
              Clear all
            </button>
          </div>
        ) : null}
      </div>

      <V2Table
        columns={tableColumns}
        data={feesNeedYear ? [] : filteredContacts}
        rowKey="id"
        storageKey="contacts_table_column_widths"
        loading={!feesNeedYear && (isContactsLoading || !contactsData)}
        notice={feesNeedYear ? 'Choose an academic year and class' : null}
        empty={
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full flex items-center justify-center">
              <svg
                className="w-7 h-7 text-[#94A3B8]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
            <span className="text-sm font-medium text-[#475569]">
              {contactsFiltered ? 'No matching results' : 'No contacts found'}
            </span>
            {contactsFiltered ? (
              <button
                type="button"
                onClick={clearContactsFilters}
                className="text-sm font-semibold text-[#50AB3D] hover:text-primary-hover"
              >
                Clear Filters
              </button>
            ) : (
              <PermissionGuard permission={PERMISSIONS.CONTACTS.CREATE}>
                <Button
                  variant="primary"
                  onClick={() => {
                    setSelectedContact(null);
                    setIsModalOpen(true);
                  }}
                  className="h-10 max-w-full !px-3 !text-sm"
                  icon={<PlusIcon />}
                >
                  Create Contact
                </Button>
              </PermissionGuard>
            )}
          </div>
        }
        onRowClick={(contact) => {
          setViewingContact(contact);
          setIsViewModalOpen(true);
        }}
        isRowSelected={(contact) => isSelected(contact.id)}
        pagination={{
          currentPage: page,
          totalCount: count,
          itemsPerPage: 10,
          onChange: setPage,
        }}
      />

      <SideDrawer
        className="contacts-panel"
        isOpen={isImportInfoModalOpen}
        labelledBy={importDialogId}
        onClose={() => {
          if (!isImporting) handleCloseImportModal();
        }}
        width="xl"
        zIndexClass="z-[60]"
        showClose={false}
        bodyClassName="p-0 min-h-0 custom-scrollbar"
        headerClassName="!px-4 !py-3.5 sm:!px-6 sm:!py-5"
        footerClassName="!px-4 !py-3.5 sm:!px-6 sm:!py-5"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <h2
              id={importDialogId}
              className="text-[18px] sm:text-[20px] leading-[26px] sm:leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display"
            >
              Import Contacts
            </h2>
            <button
              type="button"
              onClick={handleCloseImportModal}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              aria-label="Close dialog"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              disabled={!importFile || isImporting}
              onClick={handleSubmitImport}
              className="inline-flex h-[44px] items-center justify-center gap-[6px] rounded-[8px] px-5 text-[15px] font-medium text-white transition-colors focus:outline-none disabled:cursor-not-allowed w-full sm:w-auto"
              style={{
                backgroundColor:
                  importFile && !isImporting ? '#50AB3D' : '#A8D49E',
              }}
            >
              {isImporting ? 'Importing...' : 'Submit'}
              <span aria-hidden className="text-lg leading-none">
                →
              </span>
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-4 sm:gap-5 px-4 sm:px-6 py-4 sm:py-5 font-sf-pro-display bg-white">
          {/* Instruction banner */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#F5F5F5] px-3.5 sm:px-4 py-3 sm:py-3.5 rounded-[8px] shrink-0">
            <p className="text-[13px] sm:text-[14px] leading-[20px] sm:leading-[22px] text-[#868686] flex-1 min-w-0">
              Please ensure that your file in{' '}
              <span className="font-semibold text-[#1F2241]">
                Excel format (.xlsx)
              </span>{' '}
              or{' '}
              <span className="font-semibold text-[#1F2241]">CSV format</span>{' '}
              before importing. One CustomerType cell can hold several names,
              split by comma, semicolon, or pipe.
            </p>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center justify-center gap-2 shrink-0 h-[40px] px-4 rounded-[8px] bg-[#50AB3D] text-white text-[13px] sm:text-[14px] font-medium hover:bg-[#2F8E29] transition-colors focus:outline-none whitespace-nowrap w-full sm:w-auto"
            >
              <FileDocIcon className="w-4 h-4" />
              Download Template
            </button>
          </div>

          {windexConnected ? (
            <WindexYearSync
              companyId={currentCompanyId}
              onSynced={(year) => {
                setWindexFilters({ ...emptyWindexFilters(), year });
                setActiveWindexFilters((prev) =>
                  prev.includes('year') ? prev : [...prev, 'year'],
                );
                setPage(1);
                clearSelection();
              }}
            />
          ) : null}

          {/* Expected Format Preview heading outside gray box */}
          <div className="flex flex-col gap-3">
            <h3 className="text-[14px] sm:text-[15px] leading-[22px] font-semibold text-[#50AB3D] shrink-0">
              Expected Format Preview
            </h3>

            {/* Table + Upload share common #F5F5F5 background */}
            <div className="flex flex-col gap-4 bg-[#F5F5F5] rounded-[12px] p-3 sm:p-4">
              <div className="overflow-x-auto">
                <DashboardTable>
                  <DashboardTableHeader>
                    <th className="!py-3 px-3 text-left !text-[13px]">Sr</th>
                    <th className="!py-3 px-3 text-left !text-[13px]">
                      Phone*
                    </th>
                    <th className="!py-3 px-3 text-left !text-[13px]">Name</th>
                    <th className="!py-3 px-3 text-left !text-[13px]">Email</th>
                    <th className="!py-3 px-3 text-left !text-[13px]">
                      CustomerType
                    </th>
                  </DashboardTableHeader>
                  <DashboardTableBody>
                    {IMPORT_PREVIEW_ROWS.map((row) => (
                      <DashboardTableRow
                        key={row.sr}
                        className="!h-[48px] !cursor-default hover:!bg-transparent"
                      >
                        <td className="py-3 px-3 text-[13px] text-[#1F2241]">
                          {row.sr}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-[#1F2241]">
                          {row.phone}
                        </td>
                        <td className="py-3 px-3 text-[13px] font-medium text-[#1F2241]">
                          {row.name}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-[#1F2241]">
                          {row.email || '-'}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-[#1F2241]">
                          {row.customerType}
                        </td>
                      </DashboardTableRow>
                    ))}
                  </DashboardTableBody>
                </DashboardTable>
              </div>

              <div className="flex flex-col gap-2.5 shrink-0">
                <h3 className="text-[15px] leading-[22px] font-semibold text-[#1F2241]">
                  Upload File
                </h3>
                <div className="relative border border-[#D0D5D1] rounded-[8px] px-4 py-4 flex flex-col items-center justify-center bg-white hover:bg-[#FAFAFA] transition-colors">
                  <input
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleImportFileChange}
                    onClick={(e) => {
                      e.target.value = null;
                    }}
                  />
                  <div className="flex flex-col items-center gap-1.5 text-center pointer-events-none">
                    <div className="w-8 h-8 rounded-[6px] bg-[#EBF4EA] flex items-center justify-center text-[#50AB3D]">
                      <FileDocIcon className="w-4 h-4" />
                    </div>
                    {importFile ? (
                      <span className="text-[14px] font-medium text-[#1F2241]">
                        {importFile.name}
                      </span>
                    ) : (
                      <>
                        <p className="text-[14px] leading-[18px]">
                          <span className="font-semibold text-[#50AB3D]">
                            Click To Upload
                          </span>{' '}
                          <span className="text-[#868686]">
                            or drag and drop
                          </span>
                        </p>
                        <p className="text-[12px] leading-[16px] text-[#868686]">
                          XLSX or CSV
                        </p>
                      </>
                    )}
                  </div>
                  {googleWorkspaceConnected && (
                    <button
                      type="button"
                      onClick={openClientDrivePicker}
                      disabled={isDrivePickerLoading || isImporting}
                      className="absolute top-2.5 right-2.5 z-10 inline-flex w-10 h-10 items-center justify-center rounded-[8px] border border-[#D0D5D1] bg-white hover:bg-[#FAFAFA] hover:border-[#50AB3D] shadow-xs transition-colors disabled:opacity-60"
                      aria-label="Import from Google Drive"
                      title={
                        isDrivePickerLoading
                          ? 'Opening Google Drive'
                          : 'Google Drive'
                      }
                    >
                      <GoogleDriveIcon className="w-6 h-6" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SideDrawer>

      <SideDrawer
        className="contacts-panel"
        isOpen={isModalOpen}
        labelledBy={contactDialogId}
        onClose={() => {
          if (!isPending) {
            setIsModalOpen(false);
            setSelectedContact(null);
            setApiErrors({});
          }
        }}
        width="lg"
        showClose={false}
        bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
        headerClassName="!px-6 !py-5"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <h2
              id={contactDialogId}
              className="text-[20px] leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display"
            >
              {selectedContact ? 'Update Contact' : 'Create Contact'}
            </h2>
            <button
              type="button"
              onClick={() => {
                if (!isPending) {
                  setIsModalOpen(false);
                  setSelectedContact(null);
                  setApiErrors({});
                }
              }}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              aria-label="Close dialog"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
      >
        <ContactForm
          initialContact={selectedContact}
          isPending={isPending}
          apiErrors={apiErrors}
          customerTypes={customerTypes}
          onSubmit={(payload) => {
            if (selectedContact) {
              handleUpdateContact({
                companyId: currentCompanyId,
                clientId: selectedContact.id,
                payload,
              });
            } else {
              handleCreateContact({
                companyId: currentCompanyId,
                payload,
              });
            }
          }}
        />
      </SideDrawer>

      <ConfirmDeleteModal
        isOpen={!!contactToDelete}
        title="Delete Contact"
        description="This contact and their information will be permanently deleted."
        onCancel={() => setContactToDelete(null)}
        onConfirm={() => {
          executeDeleteContact({
            companyId: currentCompanyId,
            clientId: contactToDelete,
          });
          setContactToDelete(null);
        }}
      />

      <ConfirmDeleteModal
        isOpen={contactsToDeleteBulk}
        title="Delete Selected Contacts"
        description={
          matchedCount == null
            ? 'Counting contacts...'
            : `Delete ${matchedCount} contact(s)? They leave the list.`
        }
        onCancel={() => {
          setContactsToDeleteBulk(false);
          setMatchedCount(null);
        }}
        onConfirm={() => {
          if (matchedCount == null || isPreviewing) return;
          executeBatchDelete();
        }}
        isPending={isDeletingBatch || isPreviewing}
      />

      <Modal
        isOpen={bulkTypeOpen}
        onClose={() => {
          if (isSettingTypes) return;
          setBulkTypeOpen(false);
          setMatchedCount(null);
          setBulkTypeError('');
        }}
        title="Customer type"
        description={
          matchedCount == null
            ? 'Counting contacts...'
            : `Replace types on ${matchedCount} contact(s). Empty selection clears every type.`
        }
        footer={
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              disabled={isSettingTypes}
              onClick={() => {
                setBulkTypeOpen(false);
                setMatchedCount(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={isSettingTypes || isPreviewing || matchedCount == null}
              onClick={() => executeBulkTypes()}
            >
              {isSettingTypes ? 'Saving...' : 'Save'}
            </Button>
          </div>
        }
      >
        <CustomSelect
          multiple
          options={customerTypes.map((t) => ({
            value: String(t.id),
            label: t.name,
          }))}
          value={bulkTypeIds}
          onChange={(val) => {
            setBulkTypeIds(val);
            setBulkTypeError('');
          }}
          clearable
          searchable
          placeholder="Select customer types"
          disabled={isSettingTypes}
          error={Boolean(bulkTypeError)}
          className="w-full h-11"
        />
        {bulkTypeError && (
          <p className="mt-2 text-xs font-medium text-status-error">
            {bulkTypeError}
          </p>
        )}
      </Modal>

      {importResult && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card-background w-full max-w-lg rounded-card border border-border-default animate-fadeIn flex flex-col max-h-[90vh] relative text-left">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-default">
              <h3 className="text-h6 font-bold text-text-primary">
                Import Results
              </h3>
              <button
                onClick={() => setImportResult(null)}
                className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center hover:opacity-95 transition-all focus:outline-none shrink-0"
              >
                <svg
                  className="w-3 h-3 stroke-[3]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex flex-col gap-4">
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="bg-input-background border border-border-default rounded-xl p-4 flex flex-col items-center justify-center text-center">
                  <span className="text-2xl font-bold text-text-primary mb-1">
                    {importResult.total_records}
                  </span>
                  <span className="text-xs text-text-secondary">Total</span>
                </div>
                <div className="bg-success/10 border border-success/20 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                  <span className="text-2xl font-bold text-success mb-1">
                    {importResult.imported_count}
                  </span>
                  <span className="text-xs text-success">Imported</span>
                </div>
                <div className="bg-error/10 border border-error/20 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                  <span className="text-2xl font-bold text-error mb-1">
                    {importResult.failed_count}
                  </span>
                  <span className="text-xs text-error">Failed</span>
                </div>
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h4 className="text-sm font-semibold text-text-primary">
                    Errors
                  </h4>
                  <div className="bg-input-background border border-border-default rounded-xl p-4 max-h-60 overflow-y-auto custom-scrollbar flex flex-col gap-3">
                    {importResult.errors.map((error, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col gap-1 text-sm border-b border-border-default pb-2 last:border-0 last:pb-0"
                      >
                        <span className="text-error font-medium">
                          Row {error.row}: {formatErrorString(error.error)}
                        </span>
                        <span className="text-text-secondary text-xs">
                          {error.name && `Name: ${error.name}`}
                          {error.name && error.phone && ' | '}
                          {error.phone && `Phone: ${error.phone}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-border-default flex justify-end">
              <Button
                variant="primary"
                onClick={() => setImportResult(null)}
                className="w-[120px] h-[48px] flex items-center justify-center"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <CustomerTypesModal
        isOpen={isTypesModalOpen}
        onClose={() => setIsTypesModalOpen(false)}
        currentCompanyId={currentCompanyId}
      />

      <ContactDetailsModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        companyId={currentCompanyId}
        contactId={viewingContact?.id}
      />

      <ContactsExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExportDirect={handleExportClients}
        isExportingDirect={isExporting}
        selectedCount={selectedCount}
        totalCount={count}
        hasActiveFilters={Boolean(debouncedSearch || typeFilter)}
        companyId={currentCompanyId}
        activeFilters={{
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(typeFilter ? { filter: typeFilter } : {}),
        }}
        selectedIds={selectMode === 'ids' ? Array.from(pickedIds) : []}
        excludedIds={
          selectMode === 'all_matching' ? Array.from(excludedIds) : []
        }
      />
    </DashboardPage>
  );
}
